import { skipToken } from '@reduxjs/toolkit/query';
import {
  ConversationLogDto,
  CursorMetaDto,
  useConversationLogListPaginatedControllerConversationLogListPaginatedQuery,
} from '@shared/services/conversationApiService/apiService';
import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { resolveSocketIoConnection } from '@shared/utils/serviceUrl';

const { url: WS_URL, path: WS_PATH } = resolveSocketIoConnection(
  import.meta.env.VITE_CONVERSATION_API_URL,
);
const MESSAGES_LIMIT_PER_REQUEST = 20;

interface WsException {
  status: string;
  message: string;
}

export const useWebSocketConversation = (
  conversationId: string,
  onError?: <T>(error: T) => void,
) => {
  const [isConnected, setIsConnected] = useState(false);
  const [accumulatedData, setAccumulatedData] = useState<ConversationLogDto[]>([]);
  const [meta, setMeta] = useState<CursorMetaDto | undefined>();
  const [cursor, setCursor] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [socket, setSocket] = useState<Socket | null>(null);

  const resetConversationHistory = useCallback(() => {
    setAccumulatedData([]);
    setMeta(undefined);
    setCursor(null);
    setInitialLoading(true);
  }, []);

  const appendUniqueMessages = useCallback((messages: ConversationLogDto[]) => {
    setAccumulatedData((prev) => {
      const existingIds = new Set(prev.map((msg) => msg.id));
      const newMessages = messages.filter((msg) => !existingIds.has(msg.id));

      if (newMessages.length === 0) {
        return prev;
      }

      return [...prev, ...newMessages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    });
  }, []);

  const subscribeForConversationLogs = useCallback(() => {
    socket?.emit('subscribe_to_conversation_message', {
      conversationId,
    });
  }, [socket, conversationId]);

  const {
    data: oldMessages,
    error,
  } = useConversationLogListPaginatedControllerConversationLogListPaginatedQuery(
    conversationId
      ? {
          conversationId: conversationId,
          limit: MESSAGES_LIMIT_PER_REQUEST,
          cursor: cursor!,
        }
      : skipToken,
  );

  useEffect(() => {
    resetConversationHistory();
  }, [conversationId, resetConversationHistory]);

  useEffect(() => {
    if (oldMessages?.data) {
      setMeta(oldMessages.meta);
      appendUniqueMessages(oldMessages.data);
      if (oldMessages.meta.hasMore) {
        setCursor(oldMessages.meta.cursor);
      } else {
        setInitialLoading(false);
      }
    }
  }, [oldMessages, appendUniqueMessages]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken')!;

    const newSocket = io(WS_URL, {
      path: `${WS_PATH}/socket.io`,
      transports: ['websocket'],
      autoConnect: false,
      auth: {
        token,
      },
      query: {
        conversationId: conversationId,
        limit: MESSAGES_LIMIT_PER_REQUEST,
        cursor,
      },
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [conversationId]);

  useEffect(() => {
    if (!socket) return;

    const onConnect = () => {
      setIsConnected(true);
      subscribeForConversationLogs();
    };
    const onDisconnect = () => setIsConnected(false);
    const onConversation = (value: {
      result: ConversationLogDto;
      meta: CursorMetaDto | undefined;
    }) => {
      setMeta(value.meta);
      appendUniqueMessages([value.result]);
    };
    const onException = (error: WsException) => {
      socket.disconnect();

      if (onError) {
        onError(error);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('conversation_message', onConversation);
    socket.on('exception', (e) => onException(e));

    socket.connect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('conversation_message', onConversation);
      socket.off('exception', onException);
    };
  }, [socket, subscribeForConversationLogs, onError, appendUniqueMessages]);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  return {
    isConnected,
    initialLoading,
    conversationData: { data: accumulatedData, meta: meta },
    resetConversationHistory,
  };
};
